import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export function AuthDialog({ open, onOpenChange, defaultRole = "family", defaultMode = "login" }) {
  const { setUser } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: defaultRole, phone: "", sms_opt_in: false });
  const [errors, setErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (mode === "register" && !form.name.trim()) errs.name = "Please enter your name.";
    if (!form.email.trim()) errs.email = "Please enter your email.";
    if (!form.password) errs.password = "Please enter a password.";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please complete the required fields."); return; }
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(data);
        toast.success(`Welcome back, ${data.name}`);
      } else {
        const { data } = await api.post("/auth/register", form);
        setUser(data);
        toast.success("Account created");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/search";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#1C211F]">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </DialogTitle>
          <DialogDescription>
            {mode === "login" ? "Log in to save facilities and contact communities." : "Join CareBed to favorite facilities, send inquiries, or manage your listings."}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid grid-cols-2 w-full bg-[#EFEEE8]">
            <TabsTrigger value="login" data-testid="tab-login">Log in</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Sign up</TabsTrigger>
          </TabsList>

          <TabsContent value={mode} className="mt-4">
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label htmlFor="auth-name">Full name</Label>
                  <Input id="auth-name" data-testid="auth-name" required value={form.name}
                    aria-invalid={!!errors.name} aria-describedby={errors.name ? "auth-name-err" : undefined}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
                  {errors.name && <p id="auth-name-err" className="text-xs text-[#C0392B]">{errors.name}</p>}
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="auth-email">Email</Label>
                <Input id="auth-email" data-testid="auth-email" type="email" required value={form.email}
                  aria-invalid={!!errors.email} aria-describedby={errors.email ? "auth-email-err" : undefined}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
                {errors.email && <p id="auth-email-err" className="text-xs text-[#C0392B]">{errors.email}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="auth-password">Password</Label>
                <Input id="auth-password" data-testid="auth-password" type="password" required value={form.password}
                  aria-invalid={!!errors.password} aria-describedby={errors.password ? "auth-password-err" : undefined}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
                {errors.password && <p id="auth-password-err" className="text-xs text-[#C0392B]">{errors.password}</p>}
              </div>
              {mode === "register" && (
                <div className="space-y-2">
                  <Label>I am a...</Label>
                  <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}
                    className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center gap-2 border rounded-xl p-3 cursor-pointer ${form.role === "family" ? "border-[#1A365D] bg-[#E3F3ED]" : "border-[#E8E6DF]"}`}>
                      <RadioGroupItem value="family" data-testid="role-family" />
                      <span className="text-sm">Family / Planner</span>
                    </label>
                    <label className={`flex items-center gap-2 border rounded-xl p-3 cursor-pointer ${form.role === "director" ? "border-[#1A365D] bg-[#E3F3ED]" : "border-[#E8E6DF]"}`}>
                      <RadioGroupItem value="director" data-testid="role-director" />
                      <span className="text-sm">Facility Director</span>
                    </label>
                  </RadioGroup>
                </div>
              )}
              {mode === "register" && form.role === "director" && (
                <div className="space-y-3 border border-[#E8E6DF] rounded-xl p-3 bg-[#FAFAF8]" data-testid="director-sms-section">
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-phone">Mobile phone (for bed-availability texts)</Label>
                    <Input id="signup-phone" data-testid="signup-phone" type="tel" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(303) 555-0123" />
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="sms-optin-label">
                    <Checkbox checked={form.sms_opt_in} onCheckedChange={(v) => setForm({ ...form, sms_opt_in: !!v })}
                      data-testid="sms-optin-checkbox" className="mt-0.5" />
                    <span className="text-xs leading-relaxed text-[#4A534E]">
                      I agree to receive recurring automated text messages from <b>CareBed</b> (e.g., requests to update my facility's bed availability) at the phone number provided. Consent is not a condition of any purchase. Message &amp; data rates may apply; message frequency varies. Reply <b>STOP</b> to opt out or <b>HELP</b> for help. See our{" "}
                      <a href="/sms-terms" target="_blank" className="text-[#0077B6] underline">SMS Terms</a> and{" "}
                      <a href="/privacy" target="_blank" className="text-[#0077B6] underline">Privacy Policy</a>.
                    </span>
                  </label>
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="auth-submit"
                className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Log in" : "Create account"}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-[#E8E6DF]" />
              <span className="text-xs text-[#5C6660] uppercase tracking-wider">or</span>
              <div className="h-px flex-1 bg-[#E8E6DF]" />
            </div>
            <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login"
              className="w-full rounded-xl h-11 border-[#E8E6DF]">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-2" />
              Continue with Google
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
